import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { 
  Shield, CheckCircle, Trash2, FileText, HelpCircle, Star, 
  Building2, AlertTriangle, Loader2, Undo2, XCircle 
} from 'lucide-react';
import LoadingScreen from '../components/LoadingScreen';

// Email Admin
const ADMIN_EMAIL = 'info@tusciahub.it';

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('companies'); 
  
  // Dati
  const [companies, setCompanies] = useState([]);
  const [notes, setNotes] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [reviews, setReviews] = useState([]);
  
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const checkAdmin = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email !== ADMIN_EMAIL) { window.location.href = '/'; return; }
        setIsAdmin(true);
        await fetchAllData();
        setLoading(false);
    };
    checkAdmin();
  }, []);

  const fetchAllData = async () => {
      try {
        // 1. Aziende (Tutte)
        const { data: cData } = await supabase.from('profiles').select('*').eq('role', 'azienda').order('created_at', { ascending: false });
        setCompanies(cData || []);

        // 2. Dispense (Pending)
        const { data: nData } = await supabase.from('notes').select('*, profiles(email)').eq('is_approved', false).order('created_at', { ascending: false });
        setNotes(nData || []);

        // 3. Domande (Pending)
        const { data: qData } = await supabase.from('exam_questions').select('*, profiles(email)').eq('is_approved', false).order('created_at', { ascending: false });
        setQuestions(qData || []);

        // 4. Recensioni (Pending)
        const { data: rData } = await supabase.from('reviews').select('*, profiles(email), notes(title)').eq('is_approved', false).order('created_at', { ascending: false });
        setReviews(rData || []);

      } catch (e) { console.error(e); }
  };

  // --- FUNZIONE MODIFICATA PER CANCELLARE ANCHE I FILE ---
  const handleAction = async (table, id, action) => {
    if (action === 'delete' && !window.confirm("Sei sicuro di voler ELIMINARE definitivamente?")) return;
    
    try {
      if (action === 'delete') {
          // 1. RECUPERO IL RECORD PRIMA DI CANCELLARLO
          // Ci serve per avere l'URL del file da eliminare
          const { data: item, error: fetchError } = await supabase
            .from(table)
            .select('*')
            .eq('id', id)
            .single();
          
          if (fetchError) throw fetchError;

          // 2. CONFIGURO I BUCKET IN BASE ALLA TABELLA
          // Qui puoi aggiungere housing o jobs in futuro
          let bucketName = null;
          let urlField = null;

          if (table === 'notes') {
              bucketName = 'appunti';
              urlField = 'file_url';
          } 
          // ESEMPI PER QUANDO AGGIUNGERAI ALTRE TABELLE:
          else if (table === 'housing') { 
              bucketName = 'housing-images'; // Controlla il nome esatto del bucket
              urlField = 'image_url';
          } 
          else if (table === 'jobs') { 
              bucketName = 'jobs-files';     // Controlla il nome esatto del bucket
              urlField = 'logo_url';         // O 'image_url'
          }

          // 3. CANCELLO IL FILE DALLO STORAGE (SE ESISTE IL BUCKET E L'URL)
          if (bucketName && item && item[urlField]) {
              const fileUrl = item[urlField];
              // Estraggo il percorso relativo dopo il nome del bucket
              // Esempio URL: .../storage/v1/object/public/appunti/cartella/file.pdf
              const urlParts = fileUrl.split(`/${bucketName}/`);
              
              if (urlParts.length > 1) {
                  const filePath = urlParts[1];
                  console.log(`Elimino file da ${bucketName}: ${filePath}`);
                  
                  const { error: storageError } = await supabase.storage
                    .from(bucketName)
                    .remove([filePath]);

                  if (storageError) console.error("Errore Storage:", storageError);
              }
          }

          // 4. INFINE CANCELLO IL RECORD DAL DB
          await supabase.from(table).delete().eq('id', id);
          showToast("Eliminato con successo (File e Dati).");
      
      } else if (action === 'approve') {
          // Approva
          await supabase.from(table).update({ is_approved: true }).eq('id', id);
          showToast("Approvato con successo!");
      
      } else if (action === 'revoke') {
          // Revoca (Torna in attesa)
          await supabase.from(table).update({ is_approved: false }).eq('id', id);
          showToast("Approvazione revocata. L'azienda è tornata in attesa.");
      }
      
      // Ricarica i dati
      await fetchAllData(); 
    } catch (e) { 
        console.error(e);
        showToast("Errore: " + e.message, "error"); 
    }
  };

  if (loading) return <LoadingScreen />;
  if (!isAdmin) return null;

  // Filtri Aziende
  const pendingCompanies = companies.filter(c => !c.is_approved);
  const approvedCompanies = companies.filter(c => c.is_approved);

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20 px-6 font-sans text-slate-800">
      
      {toast && (
        <div className={`fixed bottom-10 left-1/2 -translate-x-1/2 z-50 px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 border ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'}`}>
            <span className="font-bold text-sm">{toast.message}</span>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
            <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-100"><Shield className="text-red-500" size={32}/></div>
            <div>
                <h1 className="text-3xl font-black text-slate-900">Admin Dashboard</h1>
                <p className="text-slate-500 font-medium">Pannello di controllo TusciaHub.</p>
            </div>
        </div>
        
        {/* TABS DI NAVIGAZIONE */}
        <div className="flex gap-3 border-b border-slate-200 mb-8 overflow-x-auto pb-4 scrollbar-hide">
            <TabButton active={activeTab === 'companies'} onClick={() => setActiveTab('companies')} icon={<Building2 size={18}/>} label={`Aziende (${pendingCompanies.length})`} />
            <TabButton active={activeTab === 'notes'} onClick={() => setActiveTab('notes')} icon={<FileText size={18}/>} label={`Dispense (${notes.length})`} />
            <TabButton active={activeTab === 'questions'} onClick={() => setActiveTab('questions')} icon={<HelpCircle size={18}/>} label={`Domande (${questions.length})`} />
            <TabButton active={activeTab === 'reviews'} onClick={() => setActiveTab('reviews')} icon={<Star size={18}/>} label={`Recensioni (${reviews.length})`} />
        </div>

        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 min-h-[500px]">
            
            {/* --- SEZIONE AZIENDE --- */}
            {activeTab === 'companies' && (
                <div className="space-y-10">
                    
                    {/* 1. IN ATTESA */}
                    <div className="space-y-4">
                        <h3 className="text-xl font-black text-amber-600 flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center"><AlertTriangle size={18}/></div>
                            In Attesa di Approvazione
                        </h3>
                        
                        {pendingCompanies.length === 0 ? (
                            <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-8 text-center text-slate-400">
                                Nessuna nuova richiesta aziendale.
                            </div>
                        ) : (
                            pendingCompanies.map(c => (
                                <div key={c.id} className="flex flex-col md:flex-row items-center justify-between p-6 bg-amber-50 border border-amber-100 rounded-3xl gap-6 shadow-sm hover:shadow-md transition-all">
                                    <div>
                                        <div className="font-black text-xl text-slate-800">{c.username}</div>
                                        <div className="text-sm text-slate-600 font-medium mt-1">{c.email}</div>
                                        <span className="inline-block mt-3 text-[10px] uppercase font-bold bg-white text-amber-600 px-3 py-1 rounded-full border border-amber-200">Nuova Richiesta</span>
                                    </div>
                                    <div className="flex gap-3 w-full md:w-auto">
                                        <BtnAction onClick={()=>handleAction('profiles',c.id,'approve')} color="emerald" icon={<CheckCircle size={18}/>} label="Accetta"/>
                                        <BtnAction onClick={()=>handleAction('profiles',c.id,'delete')} color="rose" icon={<Trash2 size={18}/>} label="Rifiuta"/>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <hr className="border-slate-100"/>

                    {/* 2. GIÀ APPROVATE (Reso Funzionale) */}
                    <div className="space-y-4">
                        <h3 className="text-xl font-black text-slate-700 flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><Building2 size={18}/></div>
                            Partner Attivi ({approvedCompanies.length})
                        </h3>

                        {approvedCompanies.length === 0 ? (
                            <p className="text-slate-400 italic pl-2">Nessun partner attivo.</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {approvedCompanies.map(c => (
                                    <div key={c.id} className="p-5 border border-slate-200 rounded-3xl flex justify-between items-start bg-slate-50 hover:bg-white hover:border-indigo-200 hover:shadow-md transition-all group">
                                        <div>
                                            <div className="font-bold text-slate-800 text-lg">{c.username}</div>
                                            <div className="text-xs text-slate-500 font-medium mb-3">{c.email}</div>
                                            <div className="flex items-center gap-1 text-emerald-600 text-xs font-bold bg-emerald-50 px-2 py-1 rounded-lg w-fit">
                                                <CheckCircle size={12}/> Verificato
                                            </div>
                                        </div>
                                        
                                        {/* Azioni Gestione Partner */}
                                        <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button 
                                                onClick={() => handleAction('profiles', c.id, 'revoke')}
                                                className="p-2 bg-white border border-slate-200 text-amber-500 rounded-xl hover:bg-amber-50 hover:border-amber-200 transition"
                                                title="Sospendi / Revoca Approvazione"
                                            >
                                                <Undo2 size={18}/>
                                            </button>
                                            <button 
                                                onClick={() => handleAction('profiles', c.id, 'delete')}
                                                className="p-2 bg-white border border-slate-200 text-rose-500 rounded-xl hover:bg-rose-50 hover:border-rose-200 transition"
                                                title="Elimina Definitivamente"
                                            >
                                                <Trash2 size={18}/>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* --- ALTRE TABS (Rimaste invariate ma stilizzate) --- */}
            {activeTab !== 'companies' && (
                <div className="space-y-4">
                    {/* Logica generica per note, questions, reviews */}
                    {(activeTab === 'notes' ? notes : activeTab === 'questions' ? questions : reviews).length === 0 ? (
                        <EmptyState msg="Nessun elemento in attesa."/>
                    ) : (
                        (activeTab === 'notes' ? notes : activeTab === 'questions' ? questions : reviews).map(item => (
                            <div key={item.id} className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                                <div>
                                    <div className="font-bold text-slate-800 line-clamp-1">
                                        {item.title || item.question_text || `Recensione di ${item.profiles?.email}`}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        {item.subject || item.subject_id} • {item.profiles?.email}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {item.file_url && <a href={item.file_url} target="_blank" rel="noreferrer" className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold hover:bg-slate-50">Vedi</a>}
                                    <BtnAction onClick={()=>handleAction(activeTab === 'notes' ? 'notes' : activeTab === 'questions' ? 'exam_questions' : 'reviews', item.id, 'approve')} color="emerald" icon={<CheckCircle size={16}/>} label=""/>
                                    <BtnAction onClick={()=>handleAction(activeTab === 'notes' ? 'notes' : activeTab === 'questions' ? 'exam_questions' : 'reviews', item.id, 'delete')} color="rose" icon={<Trash2 size={16}/>} label=""/>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

        </div>
      </div>
    </div>
  );
};

// Componenti Helper Belli
const TabButton = ({active, onClick, icon, label}) => (
    <button onClick={onClick} className={`flex items-center gap-2 px-5 py-3 rounded-full font-bold text-sm transition-all whitespace-nowrap ${active ? 'bg-slate-900 text-white shadow-xl shadow-slate-200 scale-105' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'}`}>
        {icon} {label}
    </button>
);

const BtnAction = ({onClick, color, icon, label}) => {
    const colors = {
        emerald: "bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-200",
        rose: "bg-white border-2 border-rose-100 text-rose-500 hover:bg-rose-50 hover:border-rose-200 shadow-slate-100"
    };
    return (
        <button onClick={onClick} className={`${colors[color]} px-4 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-lg active:scale-95`}>
            {icon} {label && <span>{label}</span>}
        </button>
    );
};

const EmptyState = ({msg}) => (
    <div className="text-center py-20 text-slate-400 flex flex-col items-center">
        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4"><CheckCircle className="text-slate-200" size={32}/></div>
        <p className="font-medium">{msg}</p>
    </div>
);

export default AdminDashboard;